import { PropsWithChildren, useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { IntlProvider, useIntl } from 'react-intl';

import { getAtlCohortId } from './atl-cohort';

function getRawCookie(name: string, allCookies: string) {
	const value = `; ${allCookies}`;
	const parts = value.split(`; ${name}=`);
	return parts.length === 2 ? parts.pop() : undefined;
}

function getEdgeTraceId(allCookies: string) {
	const edgeTraceIdValue = getRawCookie('X-Experiments-Trace-Id', allCookies);
	if (edgeTraceIdValue) {
		// Get the portion before the next semicolon
		const cookieValue = edgeTraceIdValue.split(';').shift();
		return cookieValue;
	}
	return undefined;
}

function getAnonymousId(allCookies: string) {
	const anonIdValue = getRawCookie('ajs_anonymous_id', allCookies);

	if (anonIdValue) {
		// Get the portion before the next semicolon
		let cookieValue = anonIdValue.split(';').shift();
		// Check if cookieValue is not undefined and remove %22 before and 22% after
		if (cookieValue) {
			cookieValue = cookieValue.replace(/%22/g, '');
		} else {
			console.log('Unable to retrieve a cookie value');
		}
		return cookieValue;
	}
	return undefined;
}

export function useEnglishLocale() {
	try {
		const intl = useIntl();
		return intl.locale === 'en';
	} catch (e) {
		console.error('Failed to determine current locale', e);
		return false;
	}
}

export const SentryIntlProvider = (
	props: PropsWithChildren<{
		locale: string;
		messages: Record<string, string>;
		cookies?: string;
	}>,
) => {
	useEffect(() => {
		Sentry.init({
			dsn: 'https://a48c5247caec7a2e0ad0b8ef86376ee1@o55978.ingest.sentry.io/4505919063457792',
			enabled: process.env['CLIENT_LIBRARY_ENV'] !== 'local',
			environment: process.env['CLIENT_LIBRARY_ENV'],
			release: window.BUILD_KEY,
			dist: window.BUILD_KEY,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.extraErrorDataIntegration(),
				Sentry.featureFlagsIntegration(),
			],
			// Performance Monitoring
			tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
			// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
			tracePropagationTargets: ['localhost', /^https:\/\/atlassian\.com/],
			// https://github.com/getsentry/sentry-javascript/issues/2514#issuecomment-603971338
			beforeSend: (event, hint) => {
				if (hint.originalException === 'Timeout') return null;

				// Filter out AbortErrors as they're expected when users navigate away
				// Check direct AbortError
				if (
					hint.originalException instanceof Error &&
					hint.originalException.name === 'AbortError'
				) {
					return null;
				}

				// Check for AbortError in message
				const errorMessage = String(event.exception?.values?.[0]?.value || '');
				if (
					errorMessage.includes('AbortError') ||
					errorMessage.includes('The user aborted a request') ||
					errorMessage.includes('user aborted')
				) {
					return null;
				}

				return event;
			},
			ignoreErrors: [/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
		});
	}, []);
	try {
		// used to tag back first introduction of errors to a unique deploy / commit
		Sentry.setTag('BUILD_KEY', window.BUILD_KEY);
	} catch (e) {
		console.error('Failed to add BUILD KEY');
	}

	try {
		// used to help identify the route, page, and variant that the user is on.
		Sentry.setTag('entryName', window.entryName);
		Sentry.setTag('cmsPageVariant', window.cmsPageVariant);
	} catch (e) {
		console.error('Failed to add route info');
	}

	// Set the anonymousId tag for Sentry
	try {
		const anonymousId = getAnonymousId(props.cookies ?? '');
		Sentry.setTag('anonymousId', anonymousId);
	} catch (e) {
		console.error('Failed to add anonymousId tag to Sentry');
	}

	// Set the edgeTraceId tag for Sentry
	try {
		const edgeTraceId = getEdgeTraceId(props.cookies ?? '');
		Sentry.setTag('edgeTraceId', edgeTraceId);
	} catch (e) {
		console.error('Failed to add edgeTraceId tag to Sentry');
	}

	try {
		// Retrieve anonymousId from cookie
		const anonymousId = getAnonymousId(props.cookies ?? '');
		const atlCohortId = getAtlCohortId(props.cookies) || -1;
		Sentry.setUser({ anonymousId, atlCohortId });
		Sentry.setTag('atlCohortId', atlCohortId);
	} catch (e) {
		console.error('Failed to add anonymousId and atlCohort tags to Sentry');
		console.error(e);
	}

	return (
		<Sentry.ErrorBoundary
			//@ts-ignore
			beforeCapture={(scope) => {
				scope.setTag('service', 'wac-web-fe');
			}}
		>
			<IntlProvider locale={props.locale} messages={props.messages}>
				{props.children}
			</IntlProvider>
		</Sentry.ErrorBoundary>
	);
};
